export default class DateUtils {
    static TEN_MIN_MILLISECONDS = 600000;
    static MILLIS_PER_DAY = 86400000;
    static MILLIS_PER_HOUR = 3600000;
    static MILLIS_PER_MINUTE = 60000;
    static SECONDS_PER_DAY = 86400;
    static SECONDS_PER_HOUR = 3600;
    //static fakeDate: Date; // Used on demo project to simulate a specific date
    // also doesn't work because it means all comments/actions happen at the exact same time, not over a period of time
    // I think we just need have a service that does a global reset of the demo project each day at midnight
    static get currentDate() {
        return new Date();
    }
    static get currentMillis() {
        return this.currentDate.getTime();
    }
    static get currentIso() {
        return this.currentDate.toISOString();
    }
    static parseISO(date) {
        return this.fromISO(date);
    }
    static fromISO(str) {
        if (!str)
            return null;
        // has timezone, unexpected in our app.
        if (/(Z|[+-]\d{2}:\d{2})$/.test(str)) {
            return new Date(str);
        }
        // is only date portion 
        if (str.length == 10) {
            return new Date(`${str}T00:00:00.000Z`);
        }
        // Append UTC timezone to the date string
        return new Date(`${str.slice(0, 16)}:00.000Z`);
    }
    /**
     * super simple date formatting for all of concourse
     * Returns something like 'Jun 29 2025'
     **/
    static format(date) {
        return date?.toDateString().substring(4) || "";
    }
    static formatIso(date) {
        return date ? DateUtils.format(DateUtils.fromISO(date)) : '';
    }
    /** English ordinal day + month, no year — e.g. `12th of September`. Uses UTC. */
    static formatDayOfMonth(date) {
        if (!date)
            return '';
        const day = date.getUTCDate();
        const month = date.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
        return `${DateUtils.ordinal(day)} of ${month}`;
    }
    static ordinal(n) {
        const mod100 = n % 100;
        if (mod100 >= 11 && mod100 <= 13)
            return `${n}th`;
        switch (n % 10) {
            case 1:
                return `${n}st`;
            case 2:
                return `${n}nd`;
            case 3:
                return `${n}rd`;
            default:
                return `${n}th`;
        }
    }
    // returns something like 'Jun 29 2025 10:00 AM'
    static formatWithHours(date) {
        return date?.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) || '';
    }
    static diffIso(date1, date2) {
        return (Date.parse(date1) - Date.parse(date2)) / this.MILLIS_PER_DAY;
    }
    static toIso(date) {
        return date ? date.toISOString().slice(0, 16) : '';
    }
    static secondsSinceIso(date) {
        return (this.currentMillis - Date.parse(date)) / 1000;
    }
    static periodSinceIso(iso) {
        const date = this.fromISO(iso);
        return date ? this.periodSince(date) : "";
    }
    static periodSince(date) {
        var seconds = Math.floor((this.currentMillis - date.getTime()) / 1000);
        return DateUtils.secondsToPeriod(seconds);
    }
    static secondsToPeriod(seconds) {
        var interval = seconds / 31536000;
        if (interval > 1) {
            return this.formatInterval(interval, 'year');
        }
        interval = seconds / 2592000;
        if (interval > 1) {
            return this.formatInterval(interval, 'month');
        }
        interval = seconds / 86400;
        if (interval > 1) {
            return this.formatInterval(interval, 'day');
        }
        interval = seconds / 3600;
        if (interval > 1) {
            return this.formatInterval(interval, 'hour');
        }
        interval = seconds / 60;
        if (interval > 1) {
            return this.formatInterval(interval, 'minute');
        }
        return this.formatInterval(seconds, 'second');
    }
    static formatInterval(interval, label) {
        return Math.floor(interval) + ' ' + label + (Math.floor(interval) > 1 ? 's' : '');
    }
    static periodSinceShort(date) {
        var seconds = Math.floor((this.currentMillis - date.getTime()) / 1000);
        return DateUtils.secondsToPeriodShort(seconds);
    }
    static secondsToPeriodShort(seconds) {
        if (seconds == 0)
            return '0';
        var interval = Math.abs(seconds) / 31536000;
        if (interval > 1) {
            return (seconds < 0 ? '-' : '') + Math.floor(interval) + 'y';
        }
        interval = Math.abs(seconds) / 2592000;
        if (interval > 1) {
            return (seconds < 0 ? '-' : '') + Math.floor(interval) + 'm';
        }
        interval = Math.abs(seconds) / 86400;
        if (interval > 1) {
            return (seconds < 0 ? '-' : '') + Math.floor(interval) + 'd';
        }
        interval = Math.abs(seconds) / 3600;
        if (interval > 1) {
            return (seconds < 0 ? '-' : '') + Math.floor(interval) + 'h';
        }
        interval = Math.abs(seconds) / 60;
        if (interval > 1) {
            return (seconds < 0 ? '-' : '') + Math.floor(interval) + 'min';
        }
        return (seconds < 0 ? '-' : '') + Math.floor(Math.abs(seconds)) + 's';
    }
    static getHours(textDate) {
        // 1) Must start with YYYY-MM-DDTHH:mm
        if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(textDate)) {
            throw new Error('Invalid date format, must start with YYYY-MM-DDTHH:mm');
        }
        // 2) Reject timezone designators (Z or ±HH:mm) at the end
        if (/(Z|[+-]\d{2}:\d{2})$/.test(textDate)) {
            throw new Error(`Timezone not allowed: ${textDate}`);
        }
        // Expect YYYY-MM-DDTHH:mm...
        const time = textDate.slice(11, 16); // "HH:mm"
        const [h, m] = time.split(':').map(Number);
        const hours12 = h % 12 || 12;
        const period = h >= 12 ? 'PM' : 'AM';
        return `${hours12}:${m.toString().padStart(2, '0')} ${period}`;
    }
    static getHoursWithTimezone(textDate) {
        // Must start with YYYY-MM-DDTHH:mm
        if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(textDate)) {
            throw new Error('Invalid date format, must start with YYYY-MM-DDTHH:mm');
        }
        // Accept timezone designators (Z or ±HH:mm) at the end
        if (!/(Z|[+-]\d{2}:\d{2})$/.test(textDate)) {
            throw new Error(`Timezone required: ${textDate}`);
        }
        const date = new Date(textDate);
        const h = date.getHours();
        const m = date.getMinutes();
        const hours12 = h % 12 || 12;
        const period = h >= 12 ? 'PM' : 'AM';
        return `${hours12}:${m.toString().padStart(2, '0')} ${period}`;
    }
    static sortByDate(a, b, ascending = true) {
        const dateA = new Date(a).getTime();
        const dateB = new Date(b).getTime();
        return ascending ? dateA - dateB : dateB - dateA;
    }
    static min(a, b) {
        if (a == null) {
            return b ?? null;
        }
        if (b == null) {
            return a;
        }
        const dateA = new Date(a).getTime();
        const dateB = new Date(b).getTime();
        return dateA < dateB ? a : b;
    }
    static max(a, b) {
        if (a == null) {
            return b ?? null;
        }
        if (b == null) {
            return a;
        }
        const dateA = new Date(a).getTime();
        const dateB = new Date(b).getTime();
        return dateA > dateB ? a : b;
    }
    static isEqual(a, b) {
        return Math.round(a.getTime() / this.MILLIS_PER_MINUTE) == Math.round(b.getTime() / this.MILLIS_PER_MINUTE);
    }
    static isSameDayString(date1, date2) {
        if (!date1 || !date2)
            return false;
        return date1.slice(0, 10) === date2.slice(0, 10);
    }
    static isSameDay(date1, date2) {
        return Math.round(date1.getTime() / this.MILLIS_PER_DAY) == Math.round(date2.getTime() / this.MILLIS_PER_DAY);
    }
}
